/** Fullscreen and screen wake lock, with the prefixed fallbacks Safari still needs. */

type FsDocument = Document & {
  webkitFullscreenElement?: Element;
  webkitExitFullscreen?: () => void;
};
type FsElement = HTMLElement & { webkitRequestFullscreen?: () => void };

export function isFullscreen(): boolean {
  const d = document as FsDocument;
  return !!(d.fullscreenElement ?? d.webkitFullscreenElement);
}

export async function toggleFullscreen(el: HTMLElement = document.documentElement): Promise<void> {
  const d = document as FsDocument;
  const e = el as FsElement;
  try {
    if (isFullscreen()) {
      if (d.exitFullscreen) await d.exitFullscreen();
      else d.webkitExitFullscreen?.();
    } else if (e.requestFullscreen) {
      await e.requestFullscreen({ navigationUI: 'hide' });
    } else {
      e.webkitRequestFullscreen?.();
    }
  } catch {
    // Denied (e.g. not triggered by a gesture); nothing else to do.
  }
}

let wakeLock: WakeLockSentinel | null = null;

/** Keep the screen awake while visuals play; re-acquired when the tab becomes visible again. */
export async function keepAwake(on: boolean): Promise<void> {
  if (!('wakeLock' in navigator)) return;
  try {
    if (on && !wakeLock) {
      wakeLock = await navigator.wakeLock.request('screen');
      wakeLock.addEventListener('release', () => (wakeLock = null));
    } else if (!on && wakeLock) {
      await wakeLock.release();
      wakeLock = null;
    }
  } catch {
    wakeLock = null;
  }
}

/**
 * The browser window itself is full screen (macOS's green button, ⌃⌘F, F11) rather than the page.
 * Chrome keeps its "sharing this tab" bar there; only the page's own full screen hides it.
 */
export function browserFullscreen(): boolean {
  if (isFullscreen()) return false;
  if (matchMedia('(display-mode: fullscreen)').matches) return true;
  return window.outerWidth >= screen.width && window.outerHeight >= screen.height;
}
